import { required, useTranslate } from "ra-core";
import { ArrayInput } from "@/components/admin/array-input";
import { CreateButton } from "@/components/admin/create-button";
import { Create } from "@/components/admin/create";
import { DataTable } from "@/components/admin/data-table";
import { Edit } from "@/components/admin/edit";
import { List } from "@/components/admin/list";
import { MobileRecordCard } from "@/components/admin/mobile-record-card";
import { NumberInput } from "@/components/admin/number-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextArrayInput } from "@/components/admin/text-array-input";
import { TextInput } from "@/components/admin/text-input";
import { Card, CardContent } from "@/components/ui/card";

import { TopToolbar } from "../../atomic-crm/layout/TopToolbar";
import type { DeliveryTemplate } from "../types";

const TemplateInputs = () => (
  <div className="flex flex-col gap-4">
    <TextInput source="name" validate={required()} />
    <TextInput source="description" multiline rows={2} />
    <ArrayInput source="milestones">
      <SimpleFormIterator>
        <TextInput
          source="title"
          label="resources.delivery_templates.fields.title"
          validate={required()}
        />
        <div className="grid gap-4 sm:grid-cols-2 [&>*]:min-w-0 [&>*>*]:min-w-0">
          <NumberInput
            source="offset_days"
            label="resources.delivery_templates.fields.offset_days"
            min={0}
          />
          <NumberInput
            source="amount_pct"
            label="resources.delivery_templates.fields.amount_pct"
            min={0}
            max={100}
          />
        </div>
        <TextArrayInput
          source="items"
          label="resources.delivery_templates.fields.items"
        />
      </SimpleFormIterator>
    </ArrayInput>
  </div>
);

const TemplateList = () => {
  const translate = useTranslate();
  const milestoneCount = (t: DeliveryTemplate) =>
    `${t.milestones?.length ?? 0} ${translate(
      "resources.deal_milestones.name",
      {
        smart_count: t.milestones?.length ?? 0,
      },
    ).toLowerCase()}`;
  return (
    <List
      resource="delivery_templates"
      sort={{ field: "name", order: "ASC" }}
      actions={
        <TopToolbar>
          <CreateButton />
        </TopToolbar>
      }
    >
      <DataTable<DeliveryTemplate>
        rowClick="edit"
        bulkActionButtons={false}
        mobileCard={(t) => (
          <MobileRecordCard
            title={t.name}
            subtitle={t.description}
            meta={[milestoneCount(t)]}
          />
        )}
      >
        <DataTable.Col source="name" />
        <DataTable.Col source="description" />
        <DataTable.Col source="milestones" render={milestoneCount} />
      </DataTable>
    </List>
  );
};

const TemplateCreate = () => (
  <Create
    resource="delivery_templates"
    redirect="list"
    className="mx-auto w-full max-w-3xl"
  >
    <Card>
      <CardContent>
        <SimpleForm className="max-w-none" defaultValues={{ milestones: [] }}>
          <TemplateInputs />
        </SimpleForm>
      </CardContent>
    </Card>
  </Create>
);

const TemplateEdit = () => (
  <Edit
    resource="delivery_templates"
    redirect="list"
    mutationMode="pessimistic"
    className="mx-auto w-full max-w-3xl"
  >
    <Card>
      <CardContent>
        <SimpleForm className="max-w-none">
          <TemplateInputs />
        </SimpleForm>
      </CardContent>
    </Card>
  </Edit>
);

export default {
  list: TemplateList,
  create: TemplateCreate,
  edit: TemplateEdit,
  recordRepresentation: "name",
};
